/**
 * cellError —— 格内错误提示链与红框样式契约。
 *
 * 【一条完整的因果链】`touched → 防抖 → validateValue → Form.Item validateStatus → 红框/tooltip`
 * 的每一环都在本文件，拆开排查「红框没出现」要跨多个目录开多个文件。
 *
 * 【两个错误态类名为何必须分开】见 `ADD_ERR_CLASS` 注释。
 *
 * 【本文件也是红框样式契约的唯一来源】`ERR_STYLE`（CSS 面）与 `ERR_CELL_CLASS`（选择器面）
 * 是同一份契约的两面 —— 选择器字符串里就写着那个类名，**改动时必须同步**，故放在一起。
 */
import React, { useEffect, useState } from 'react';
import { Form, Tooltip } from '@bedrock/components';
import { validateValue } from './fieldRules';
import type { FieldRules } from './types';

/**
 * 校验错误红边框（scoped）：复用 @bedrock Form.Item has-error 的 selector，覆盖内层 select/input 灰边框为红。
 *
 * ⚠️ 必须逐个列出「**边框实际所在的元素**」——各控件的可见边框不在同一层：
 *  - Select   → `.rock-select-selector`
 *  - Input    → `.rock-input-wrapper`
 *  - DatePicker / RangePicker → `.rock-picker`（其内层 `.rock-picker-input` 与原生 `input` 均为 `border-width: 0`，
 *    所以仅靠末位的 `input` 选择器虽然命中了元素，却看不到任何红色 —— 实测日期/日期区间不标红即此因）
 */
export const ERR_STYLE = ".rock-table .rock-form-item-has-error .rock-select-selector, .rock-table .rock-form-item-has-error .rock-input-wrapper, .rock-table .rock-form-item-has-error .rock-picker, .rock-table .rock-form-item-has-error input { border-color: #E55140 !important; }";

/** 标红单元格的类名（校验定位时用于在行内横向定位到出错单元格） */
export const ERR_CELL_CLASS = 'rock-form-item-has-error';

/**
 * 错误态「+添加{emptyLabel}」入口的定位标记类。
 *
 * 与 ERR_CELL_CLASS **分开**是必要的：后者兼作 Form.Item 的红框样式钩子（见 ERR_STYLE），
 * 而添加入口不是 Form.Item（其错误态是 Button 转红 + AlertCircle），染上红框样式是错的。
 *
 * 子数组为空时格内没有任何输入控件，错误只能落在添加入口上 —— 若不给它标记，
 * 校验定位会「找得到行、找不到格」，横向不滚动，用户看不到那个按钮。
 */
export const ADD_ERR_CLASS = 'pro-table-add-error';

/**
 * 输入期校验提示的防抖时长（ms）。
 *
 * 值从「空 → 非空」时，touched 置位与受控值更新可能不在同一渲染批次，会出现
 * 「先按空值标红、下一帧值到位又取消」的一帧闪烁；延迟置位可让判定落在值更新之后。
 * 同时也避免输入过程中频繁校验。
 *
 * ⚠️ 业务侧复用本能力（`useCellError`）时 **不要自己再对齐一遍这个时长** ——
 * 手写防抖正是重构前胶水点 ⑦ 的来源。
 */
export const INPUT_VALIDATE_DEBOUNCE_MS = 300;

/** 输入期错误提示的防抖开关：touched 后延迟可见；外部全量校验（forceValidate）立即可见 */
export function useDebouncedErrorVisible(touched: boolean, forceValidate?: boolean): boolean {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (forceValidate || !touched) return undefined;
    const timer = setTimeout(() => setVisible(true), INPUT_VALIDATE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [touched, forceValidate]);
  return !!forceValidate || visible;
}

export const ErrorWrap: React.FC<{ value: unknown; field: string; rules: FieldRules; showError?: boolean; item?: unknown; form?: unknown; children: React.ReactNode }> = ({ value, field, rules, showError = true, item, form, children }) => {
  // showError=false 时不计算也不展示错误：输入期只对「用户实际编辑过的本格」提示，避免扩散标红
  // form 透传给 validateValue，用于展开 FieldRuleRender（动态规则）
  const msg = showError ? validateValue(rules, field, value, item, form) : undefined;
  const hasError = !!msg;
  return (
    /* w-full：Form.Item 自身在 flex 父级（子项行）中会被 shrink，需显式撑满 cell 的 render 区域 */
    <Form.Item validateStatus={hasError ? 'error' : undefined} className="mb-0 w-full [&_.rock-form-item-label]:hidden">
      <Tooltip title={hasError ? msg : undefined} placement="top">
        {/* w-full：Form.Item 的内容层是 flex 容器，包裹层不显式撑满会被 shrink 到内容宽度
            （表现为 TextAreaPopover 这类无固定宽度子组件只有 ~192px，未铺满 cell） */}
        <div className="rounded-[6px] w-full">{children}</div>
      </Tooltip>
    </Form.Item>
  );
};
